import { FeatureHubGuard } from "@/featureFlags";
import type { LayerWithParameterUnit } from "@/layers/utility/createLayerObject";
import CompleteThreshold from "@/overlay/components/CommonParts/Threshold/CompleteThreshold";
import type { ThresholdColor } from "@/overlay/components/CommonParts/Threshold/types";
import { Button } from "@/overlay/components/CommonParts/buttons";
import { InitDateField } from "@/overlay/components/LayerStackWindow/InitDateField";
import { CalibratedToggle } from "@/overlay/components/LayerStackWindow/LayerFormElements/CalibratedToggle";
import { PoiInputFields } from "@/overlay/components/LayerStackWindow/PoiLayerPanel/PoiInputFields";
import { ModelAndWeatherParameterForm } from "@/overlay/components/ModelAndWeatherParameterForm";
import type { ParameterEditCallback } from "@/overlay/components/WeatherParameterForm";
import { FormField } from "@/overlay/components/form-field";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components/window";
import type { ColoringOptions, IconData, PoiOptions } from "@/reducer/client-models";
import type { PoiLayer } from "@/reducer/client-models/PoiLayer";
import type { PoiDataGrid } from "@/threads/DataProcessingThread/types";
import type { MapId } from "@/types/type-utils";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useEffect, useState } from "react";
import type { DraggableItemIdentifier } from "../../CommonParts/SortableList";

const PoiLayerPanel = (props: {
  mapId: MapId;
  poiOptions: PoiOptions;
  onChangeItem: (newItem: IconData) => void;
  onChangePoiOptions: (options: Partial<PoiOptions>) => void;
  onChangeColoringOptions: (coloringOptions: ColoringOptions | undefined) => void;
  onChangeLayerProps: (propsValue: Partial<PoiLayer>) => void;
  onEditParameter: ParameterEditCallback;
  onAddParameter: () => void;
  layer: PoiLayer;
}) => {
  const desktop = useDesktop();
  const [item, setItem] = useState<IconData>(props.poiOptions.item);
  const [coloringOptions, setColoringOptions] = useState<ColoringOptions | undefined>(props.poiOptions.coloringOptions);
  const poiParamUnit = props.layer.parameter_unit;
  useEffect(() => {
    setColoringOptions(props.poiOptions.coloringOptions);
    setItem(props.poiOptions.item);
  }, [props.poiOptions]);

  function triggerColoringOptionsOnChange(propsValue: Partial<ColoringOptions>) {
    if (coloringOptions) {
      const options: ColoringOptions = { ...coloringOptions, ...propsValue };
      setColoringOptions(options);
      props.onChangeColoringOptions(options);
    }
  }

  function triggerOnChange(propsValue: Partial<IconData>) {
    const newItem = { ...item, ...propsValue };
    setItem(newItem);
    props.onChangeItem(newItem);
  }

  function handleToggleLabels(hideLabels: boolean) {
    props.onChangePoiOptions({ hideLabels });
    if (!hideLabels) {
      if (!poiParamUnit) {
        props.onAddParameter();
      }
    }
  }

  function handleRemoveParameterAndThresholds() {
    setColoringOptions(undefined);
    props.onChangeColoringOptions(undefined);
  }

  function handleEditPoints() {
    desktop.dispatch({
      type: "confirmationWindow",
      windowId: instanceId(WindowKind.PoiManagerWindow),
      customProps: {
        onUpdate: (data: PoiDataGrid) => {
          triggerOnChange({ coords: data });
        },
        mapId: props.mapId.map,
        initialData: item.coords,
      },
    });
  }

  function moveThresholdIdx(dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) {
    if (!coloringOptions?.thresholds) {
      console.error("Threshold orders were swapped but no data is available");
      return;
    }
    const copyOfThresholds = [...coloringOptions.thresholds];

    const fromIdx = dragStart.index;
    let toIdx = dropTarget.index;
    if (toIdx >= copyOfThresholds.length) {
      toIdx = copyOfThresholds.length - 1;
    }
    if (toIdx < 0) {
      toIdx = 0;
    }

    const targetThreshold = copyOfThresholds.splice(fromIdx, 1)[0];
    copyOfThresholds.splice(toIdx, 0, targetThreshold);

    triggerColoringOptionsOnChange({ thresholds: copyOfThresholds });
  }

  return (
    <div>
      <PoiInputFields
        item={item}
        hideLabels={!poiParamUnit || props.layer.poiOptions.hideLabels}
        onInputChange={triggerOnChange}
        onToggleLabels={handleToggleLabels}
      />
      <FormField label={t`Points`}>
        <Button icon="my_location" onClick={handleEditPoints}>
          <Trans>Manage</Trans>
        </Button>
      </FormField>

      {!poiParamUnit && (
        <FormField label={t`Parameter`}>
          <Button icon="add" onClick={props.onAddParameter}>
            <Trans>Add Parameter</Trans>
          </Button>
        </FormField>
      )}

      {poiParamUnit && (
        <>
          <ModelAndWeatherParameterForm
            layer={props.layer as LayerWithParameterUnit}
            mapId={props.mapId}
            onEditParameter={props.onEditParameter}
            onRemoveParameter={handleRemoveParameterAndThresholds}
            includeStationModels
          />
          {hasCalibratedAvailable(props.layer.parameter_unit, props.layer.model) && (
            <CalibratedToggle value={!!props.layer.calibrated} change={props.onChangeLayerProps} />
          )}
          <FeatureHubGuard flag="api_url_suffix">
            <InitDateField
              label={t`API URL suffix`}
              defaultValue={props.poiOptions.apiUrlSuffix ?? ""}
              onChange={(apiUrlSuffix) => props.onChangePoiOptions({ apiUrlSuffix })}
            />
          </FeatureHubGuard>

          <CompleteThreshold
            onMoveIndex={moveThresholdIdx}
            updateThresholds={(newThresholds: ThresholdColor[]) =>
              triggerColoringOptionsOnChange({
                ...coloringOptions,
                thresholds: newThresholds,
              })
            }
            updateAdvancedThresholds={(newThresholds) =>
              triggerColoringOptionsOnChange({
                ...coloringOptions,
                advancedThresholds: newThresholds,
              })
            }
            thresholds={coloringOptions?.thresholds || []}
            advancedThresholds={coloringOptions?.advancedThresholds || []}
            enableAdvancedThreshold
            isPoi={true}
          />
        </>
      )}
    </div>
  );
};

export default PoiLayerPanel;
